import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  buildCommissionDeltas,
  type CommissionNettingResult,
  type CommissionPayoutRow,
} from "@/lib/commission-netting";

interface RawPayoutRow {
  project_id: string;
  user_id: string;
  locked_at: string;
  bf_actual_at_lock: number;
  commission_rate_pct: number | null;
  split_type: "percent" | "fixed";
  ownership_pct: number | null;
  fixed_amount: number | null;
  projects: {
    project_code: string | null;
    name: string;
    bf_actual: number | null;
    deleted_at: string | null;
    is_internal: boolean | null;
  } | null;
  users: { full_name: string | null } | null;
}

/**
 * Non-hook fetch: utbetalt grunnlag (commission_payouts, RLS admin-only) +
 * prosjektets LIVE bf_actual → netting-delta via buildCommissionDeltas
 * (VIB-1240). Halvåpent intervall [from, to) på locked_at, som RPC-en.
 */
export async function fetchCommissionDeltas(period: {
  from: string;
  to: string;
}): Promise<CommissionNettingResult> {
  const { data, error } = await supabase
    .from("commission_payouts")
    .select(
      "project_id, user_id, locked_at, bf_actual_at_lock, commission_rate_pct, split_type, ownership_pct, fixed_amount, projects(project_code, name, bf_actual, deleted_at, is_internal), users:user_id(full_name)",
    )
    .gte("locked_at", period.from)
    .lt("locked_at", period.to);
  if (error) throw error;

  const rows: CommissionPayoutRow[] = [];
  for (const r of (data ?? []) as unknown as RawPayoutRow[]) {
    const p = r.projects;
    if (!p || p.deleted_at || p.is_internal) continue;
    rows.push({
      project_id: r.project_id,
      project_code: p.project_code,
      project_name: p.name,
      locked_at: r.locked_at,
      bf_actual_at_lock: Number(r.bf_actual_at_lock ?? 0),
      live_bf_actual: Number(p.bf_actual ?? 0),
      commission_rate_pct: r.commission_rate_pct,
      user_id: r.user_id,
      full_name: r.users?.full_name ?? null,
      split_type: r.split_type,
      ownership_pct: r.ownership_pct,
      fixed_amount: r.fixed_amount,
    });
  }
  return buildCommissionDeltas(rows);
}

/** Netting-delta (sene endringer etter lukking) for CSV-en/rapporten. */
export function useCommissionDeltas(period: { from: string; to: string }) {
  return useQuery({
    queryKey: ["commission-deltas", { from: period.from, to: period.to }],
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchCommissionDeltas(period),
  });
}
